/**
 * Token Utility Functions
 * Helpers for debugging and managing Cognito authentication tokens
 */

/**
 * Get the Cognito access token key from localStorage
 * @returns The localStorage key for the access token, or null if not found
 */
export function getCognitoAccessTokenKey(): string | null {
  try {
    const storageKeys = Object.keys(localStorage)
    const accessTokenKey = storageKeys.find(
      (key) => key.startsWith('CognitoIdentityServiceProvider.') && key.endsWith('.accessToken')
    )
    return accessTokenKey || null
  } catch {
    return null
  }
}

/**
 * Get the Cognito access token from localStorage
 * @returns The access token string, or null if not found
 */
export function getCognitoAccessToken(): string | null {
  try {
    const tokenKey = getCognitoAccessTokenKey()
    if (tokenKey) {
      return localStorage.getItem(tokenKey)
    }
    return null
  } catch {
    return null
  }
}

/**
 * Get the Cognito ID token from localStorage
 * @returns The ID token string, or null if not found
 */
export function getCognitoIdToken(): string | null {
  try {
    const storageKeys = Object.keys(localStorage)
    const idTokenKey = storageKeys.find(
      (key) => key.startsWith('CognitoIdentityServiceProvider.') && key.endsWith('.idToken')
    )
    if (idTokenKey) {
      return localStorage.getItem(idTokenKey)
    }
    return null
  } catch {
    return null
  }
}

/**
 * Decode a JWT token payload
 * @param token The JWT token string
 * @returns The decoded payload object, or null if invalid
 */
export function decodeJWT(token: string): Record<string, unknown> | null {
  try {
    const parts = token.split('.')
    if (parts.length !== 3) {
      return null
    }

    const payload = parts[1]
    const decoded = JSON.parse(atob(payload)) as Record<string, unknown>
    return decoded
  } catch {
    return null
  }
}

/**
 * Check if a JWT token is expired
 * @param token The JWT token string
 * @returns true if expired, false if still valid, null if invalid token
 */
export function isTokenExpired(token: string): boolean | null {
  try {
    const payload = decodeJWT(token)
    if (!payload || !payload.exp) {
      return null
    }

    const expirationTime = (typeof payload.exp === 'number' ? payload.exp : 0) * 1000 // Convert to milliseconds
    const now = Date.now()
    return now > expirationTime
  } catch {
    return null
  }
}

/**
 * Get token expiration time
 * @param token The JWT token string
 * @returns Date object of expiration time, or null if invalid
 */
export function getTokenExpiration(token: string): Date | null {
  try {
    const payload = decodeJWT(token)
    if (!payload || !payload.exp) {
      return null
    }

    return new Date((typeof payload.exp === 'number' ? payload.exp : 0) * 1000)
  } catch {
    return null
  }
}

/**
 * Get all Cognito token keys from localStorage
 * @returns Array of token key strings
 */
export function getAllCognitoTokenKeys(): string[] {
  try {
    const storageKeys = Object.keys(localStorage)
    return storageKeys.filter((key) => key.startsWith('CognitoIdentityServiceProvider.'))
  } catch {
    return []
  }
}

/**
 * Clear all Cognito tokens from localStorage
 * Useful for debugging or manual sign-out
 */
export function clearAllCognitoTokens(): void {
  try {
    const cognitoKeys = getAllCognitoTokenKeys()
    cognitoKeys.forEach((key) => {
      localStorage.removeItem(key)
    })
  } catch {
    // Silent fail
  }
}

/**
 * Debug function: Log all token information to console
 */
export function debugTokenInfo(): void {
  try {
    // Access Token
    getCognitoAccessTokenKey()
    const accessToken = getCognitoAccessToken()

    if (accessToken) {
      isTokenExpired(accessToken)
      getTokenExpiration(accessToken)
      decodeJWT(accessToken)
    }

    // ID Token
    getCognitoIdToken()

    // All Cognito Keys
    getAllCognitoTokenKeys()
  } catch {
    // Silent fail
  }
}

/**
 * Add token debugging to window object for console access
 * Usage in browser console: window.debugTokens()
 */
if (typeof window !== 'undefined') {
  interface WindowWithTokenDebug extends Window {
    debugTokens?: () => void
    clearCognitoTokens?: () => void
    getAccessToken?: () => string | null
  }

  ;(window as WindowWithTokenDebug).debugTokens = debugTokenInfo
  ;(window as WindowWithTokenDebug).clearCognitoTokens = clearAllCognitoTokens
  ;(window as WindowWithTokenDebug).getAccessToken = getCognitoAccessToken
}

export const tokenUtils = {
  getCognitoAccessTokenKey,
  getCognitoAccessToken,
  getCognitoIdToken,
  decodeJWT,
  isTokenExpired,
  getTokenExpiration,
  getAllCognitoTokenKeys,
  clearAllCognitoTokens,
  debugTokenInfo,
}
